import { DefaultTheme } from "styled-components";

export const SemanticsList = [
  "alarm",
  "secondaryAlarm",
  "warning",
  "info",
  "success",
  "neutral",
  "system",
  "transparent",
  "transparentInverted",
] as const;

export type Semantics = (typeof SemanticsList)[number];

export type AlertVariant =
  | "error"
  | "error-secondary"
  | "important"
  | "success"
  | "info"
  | "neutral"
  | "alt"
  | "service";

export const variantToSemantics = (
  variant: AlertVariant | undefined,
): Semantics | undefined => {
  switch (variant) {
    case "error":
      return "alarm";
    case "error-secondary":
      return "secondaryAlarm";
    case "important":
      return "warning";
    case "success":
      return "success";
    case "info":
      return "info";
    case "neutral":
      return "neutral";
    case "alt":
    case "service":
      return undefined;
    case undefined:
      return undefined;
  }
};

export type DelayThreshold = {
  minor: number;
  moderate: number;
  major: number;
};

const delayThreshold: DelayThreshold = {
  minor: 4,
  moderate: 6,
  major: 11,
};

export const getSemanticsForDelay = (delay: number): Semantics => {
  if (delay < -1) return "info";
  if (delay >= delayThreshold.major) return "alarm";
  if (delay >= delayThreshold.moderate) return "secondaryAlarm";
  if (delay >= delayThreshold.minor) return "warning";
  return "success";
};

const getColorForSemantics = (semantics: Semantics, theme: DefaultTheme) => {
  switch (semantics) {
    case "alarm":
      return theme.colorAlarm;
    case "secondaryAlarm":
      return theme.colorSecondaryAlarm;
    case "warning":
      return theme.colorWarning;
    case "info":
      return theme.colorInfo;
    case "success":
      return theme.colorSuccess;
    case "neutral":
      return theme.colorNeutral;
    case "system":
      return theme.colorSystem;
    case "transparent":
      return "transparent";
    case "transparentInverted":
      return "transparent";
  }
};

export const getTrainSemantic = ({
  delay,
  isVisited = true,
  isCancelled,
}: {
  delay: number;
  isVisited?: boolean;
  isCancelled?: boolean;
}): Semantics | undefined => {
  if (!isVisited) return undefined;
  if (isCancelled) return undefined;

  return getSemanticsForDelay(delay);
};

/* Return the color to indicate trains delay status. If there is no major, moderate or minor delays, it return a transparent color */
export const calcDelayColor = (delay: number | null, theme: DefaultTheme) => {
  if (delay) {
    return getColorForSemantics(getSemanticsForDelay(delay), theme);
  }

  return theme.colorTransparentFill;
};
